import React, { useEffect, useState } from "react";
import { api, formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, Loader2, Send, Users as UsersIcon, ShieldCheck, ShieldAlert, AlertTriangle, MessageSquare } from "lucide-react";

const AUDIENCES = [
  { key: "agents",    label: "Data-entry agents",  icon: UsersIcon,   color: "#5AC8FA" },
  { key: "observers", label: "Reviewers",          icon: ShieldCheck, color: "#34C759" },
  { key: "admins",    label: "Admins",             icon: ShieldAlert, color: "#AF52DE" },
  { key: "all_staff", label: "All staff (a+o+a)",  icon: Megaphone,   color: "#FF9F0A" },
];

const QUICK_TEMPLATES = [
  { title: "Critical fraud alert",  body: "CRITICAL: fraud pattern detected. Freeze submissions and await verification from HQ." },
  { title: "Halt submissions",      body: "Please halt all result submissions pending clarification from the Returning Officer." },
  { title: "Verify EC8A sheets",    body: "Re-verify EC8A sheet totals for all pending PUs before submitting to the collation centre." },
  { title: "Escalate obstruction",  body: "If you are being obstructed at your PU, log an Incident with photo evidence immediately." },
];

export default function BroadcastSms() {
  const [form, setForm] = useState({ audience: "agents", message: "" });
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [confirm, setConfirm] = useState(false);

  const loadPreview = async (audience = form.audience) => {
    try {
      const { data } = await api.post("/broadcasts/sms", { message: "preview", audience, dry_run: true });
      setPreview(data);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const loadHistory = async () => {
    try { const { data } = await api.get("/broadcasts/sms", { params: { limit: 20 }}); setHistory(data); } catch {}
  };
  useEffect(() => { loadPreview(); loadHistory(); }, []);
  useEffect(() => { loadPreview(form.audience); }, [form.audience]);

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/broadcasts/sms", { message: form.message, audience: form.audience });
      if (data.delivered) toast.success(`Broadcast sent to ${data.sent} of ${data.recipients} phone${data.recipients===1?"":"s"}${data.failed?.length ? ` · ${data.failed.length} failed` : ""}`);
      else toast.warning(`Recorded but not delivered: ${data.reason}. (${data.recipients} recipient${data.recipients===1?"":"s"} on file.)`);
      setForm(f => ({ ...f, message: "" }));
      setConfirm(false);
      loadHistory();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const charCount = form.message.length;
  const overLimit = charCount > 320;

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1400px]">
      <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Emergency Comms</div>
      <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2" data-testid="broadcast-title">Broadcast SMS</h1>
      <p className="text-zinc-400 text-sm mt-2 max-w-2xl">Fire a single line to every field agent, reviewer or admin the moment something demands it. Messages carry the <span className="text-white">[Collation Watch]</span> prefix and are logged in the audit trail.</p>

      <div className="grid grid-cols-12 gap-6 mt-8">
        {/* Left: composer */}
        <div className="col-span-12 lg:col-span-8 space-y-6">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Audience</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {AUDIENCES.map(a => (
                <button key={a.key} type="button" onClick={()=>setForm(f=>({...f, audience:a.key}))} data-testid={`audience-${a.key}`}
                  className={`p-3 border text-left transition-colors ${form.audience===a.key ? "border-white bg-white/5" : "border-white/15 hover:bg-white/[0.03]"}`}>
                  <a.icon size={14} style={{ color: a.color }}/>
                  <div className="text-xs uppercase tracking-wider text-zinc-400 mt-2">{a.label}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Message</div>
              <div className={`text-xs tabular ${overLimit ? "text-red-400" : "text-zinc-500"}`}>{charCount}/320</div>
            </div>
            <textarea rows={5} value={form.message} onChange={e=>setForm(f=>({...f, message:e.target.value}))} data-testid="broadcast-message"
              placeholder="e.g. CRITICAL: over-voting flagged at PU-045. Verify EC8A totals before resubmission."
              className={`w-full bg-transparent border p-4 outline-none focus:border-white text-base resize-none ${overLimit ? "border-red-500/50" : "border-white/20"}`}/>
            <div className="mt-3 flex flex-wrap gap-2">
              {QUICK_TEMPLATES.map((t, i) => (
                <button type="button" key={i} onClick={()=>setForm(f=>({...f, message: t.body}))} data-testid={`template-${i}`}
                  className="text-[11px] uppercase tracking-wider px-2.5 py-1.5 border border-white/15 hover:bg-white/10">
                  <MessageSquare size={11} className="inline mr-1.5"/>{t.title}
                </button>
              ))}
            </div>
          </div>

          <div className="border border-red-500/30 bg-red-500/[0.06] p-4 flex items-start gap-3">
            <AlertTriangle className="text-red-400 shrink-0 mt-0.5" size={16}/>
            <div className="flex-1 text-sm text-zinc-300">
              <div className="font-semibold text-red-200">This will reach real phones once Twilio is configured.</div>
              <div className="text-xs text-zinc-400 mt-1">
                {preview
                  ? preview.twilio_configured
                    ? <>Twilio connected · will send to <span className="text-white tabular">{preview.recipients}</span> {form.audience==="all_staff"?"staff":form.audience} on file.</>
                    : <>Twilio not yet configured — messages will be recorded in the audit log but no SMS will be sent. Add TWILIO_ACCOUNT_SID / TWILIO_AUTH_TOKEN / TWILIO_PHONE_NUMBER in backend/.env.</>
                  : "Loading recipient count…"}
              </div>
            </div>
            <button type="button" onClick={()=>setConfirm(true)} disabled={!form.message.trim() || overLimit || sending} data-testid="broadcast-send-btn"
              className="px-4 py-2.5 bg-red-500 text-white text-xs uppercase tracking-[0.2em] font-semibold hover:bg-red-400 disabled:opacity-40 flex items-center gap-2 self-center whitespace-nowrap">
              {sending ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
              {sending ? "Firing…" : "Send broadcast"}
            </button>
          </div>
        </div>

        {/* Right: recent broadcasts */}
        <div className="col-span-12 lg:col-span-4">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-2">Recent broadcasts</div>
          <div className="border border-white/10 divide-y divide-white/5" data-testid="broadcast-history">
            {!history.length && <div className="p-4 text-xs text-zinc-500">Nothing yet.</div>}
            {history.map(h => {
              const delivered = (h.recipients || []).filter(r => ["delivered","sent"].includes(r.status)).length;
              const failed = (h.recipients || []).filter(r => ["failed","undelivered"].includes(r.status)).length;
              return (
                <details key={h.id} className="p-3">
                  <summary className="cursor-pointer list-none">
                    <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em]">
                      <span className="text-zinc-500">{h.audience}</span>
                      <span className={`${h.sent_count>0 ? "text-emerald-400" : "text-amber-400"}`}>{h.sent_count}/{h.recipients_count} sent</span>
                    </div>
                    <div className="text-sm mt-1 line-clamp-2">{h.message}</div>
                    <div className="text-[10px] text-zinc-500 mt-1 flex items-center gap-2">
                      <span>{(h.sent_by_email||"").split("@")[0]} · {(h.created_at||"").replace("T"," ").slice(0,16)}</span>
                      {delivered > 0 && <span className="text-emerald-400">✓ {delivered} delivered</span>}
                      {failed > 0 && <span className="text-red-400">✗ {failed} failed</span>}
                    </div>
                  </summary>
                  {(h.recipients || []).length > 0 && (
                    <div className="mt-3 border-t border-white/5 pt-2 max-h-40 overflow-y-auto" data-testid={`recipients-${h.id}`}>
                      {(h.recipients || []).map((r, i) => {
                        const st = r.status || "unknown";
                        const c = ["delivered","sent"].includes(st) ? "text-emerald-400"
                                : ["failed","undelivered"].includes(st) ? "text-red-400"
                                : st === "not_configured" ? "text-zinc-500" : "text-amber-400";
                        const icon = ["delivered","sent"].includes(st) ? "✓"
                                   : ["failed","undelivered"].includes(st) ? "✗" : "•";
                        return (
                          <div key={r.phone || i} className="flex items-center justify-between py-0.5 text-[11px]">
                            <span className="text-zinc-300 truncate">{r.phone}</span>
                            <span className={`${c} uppercase tracking-wider text-[10px] ml-2 flex items-center gap-1`}>
                              <span>{icon}</span> {st}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {(h.replies || []).length > 0 && (
                    <div className="mt-3 border-t border-emerald-500/20 pt-2" data-testid={`replies-${h.id}`}>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 mb-1.5">↩ {(h.replies || []).length} reply{(h.replies||[]).length===1?"":"ies"}</div>
                      {(h.replies || []).slice(-8).map((rp, i) => (
                        <div key={rp.id || i} className="mb-2 border-l-2 border-emerald-500/40 pl-2 flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="text-[10px] text-zinc-500">{rp.from_name || rp.from_email || rp.from} · {(rp.received_at||"").replace("T"," ").slice(11,16)}</div>
                            <div className="text-[11px] text-zinc-200 whitespace-pre-wrap">{rp.message}</div>
                          </div>
                          {rp.incident_id ? (
                            <span className="text-[10px] uppercase tracking-wider text-amber-300 whitespace-nowrap" title="Converted">✓ Incident</span>
                          ) : (
                            <button onClick={async ()=>{
                              try {
                                const { data } = await api.post(`/broadcasts/${h.id}/replies/convert`, { reply_id: rp.id, category: "other", severity: "medium" });
                                toast.success("Reply logged as Incident");
                                // optimistic update
                                rp.incident_id = data.incident_id;
                                loadHistory();
                              } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Convert failed"); }
                            }} data-testid={`convert-reply-${rp.id||i}`}
                              className="text-[10px] uppercase tracking-wider px-2 py-1 border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 whitespace-nowrap">
                              → Incident
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </details>
              );
            })}
          </div>
        </div>
      </div>

      {/* Confirm dialog */}
      {confirm && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={()=>setConfirm(false)} data-testid="broadcast-confirm">
          <div onClick={e=>e.stopPropagation()} className="bg-[#0F0F12] border border-red-500/40 max-w-md w-full p-6">
            <div className="text-xs uppercase tracking-[0.3em] text-red-400">Confirm broadcast</div>
            <h3 className="font-display text-2xl mt-2">Send to {preview?.recipients ?? 0} phone{preview?.recipients===1?"":"s"}?</h3>
            <div className="mt-4 border border-white/10 p-3 bg-black/40 text-sm text-zinc-200 whitespace-pre-wrap">
              <span className="text-zinc-500">[Collation Watch]</span> {form.message}
            </div>
            <div className="mt-2 text-[10px] text-zinc-500 uppercase tracking-wider">
              Audience: {form.audience} · Twilio: {preview?.twilio_configured ? "configured" : "not configured — will log only"}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={()=>setConfirm(false)} className="px-4 py-2 border border-white/20 text-xs uppercase tracking-[0.2em] hover:bg-white/10">Cancel</button>
              <button onClick={send} disabled={sending} data-testid="broadcast-confirm-btn"
                className="px-4 py-2 bg-red-500 text-white text-xs uppercase tracking-[0.2em] font-semibold hover:bg-red-400 disabled:opacity-40 flex items-center gap-2">
                {sending ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
                Fire it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
